/** Konva's default text face, which the canvas product labels use. */
export const CANVAS_LABEL_FONT_FAMILY = "Arial";

/** Line height the fitted labels are laid out with, as a multiple of the font size. */
export const CANVAS_LABEL_LINE_HEIGHT = 1.15;

/** Room left between the text and the product edges, as a share of each side. */
const LABEL_BOX_FILL = 0.9;

/**
 * The canvas product label size in world units: one size for the whole planogram, set from its
 * height. The POG editor's canvas sizes its product labels with this same rule.
 */
export const getCanvasLabelFontSize = (worldHeight: number) =>
  Math.max(16, (worldHeight / 600) * 12);

export type MeasureTextWidth = (text: string, fontSize: number) => number;

let measureContext: CanvasRenderingContext2D | null | undefined;

/** Text width in the label face; falls back to an average glyph width where there is no canvas. */
export const measureCanvasTextWidth: MeasureTextWidth = (text, fontSize) => {
  if (measureContext === undefined) {
    try {
      measureContext =
        typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d");
    } catch {
      measureContext = null;
    }
  }

  if (measureContext) {
    measureContext.font = `bold ${fontSize}px ${CANVAS_LABEL_FONT_FAMILY}`;
    return measureContext.measureText(text).width;
  }

  return text.length * fontSize * 0.6;
};

/**
 * The largest font size, up to `maxFontSize`, at which every line fits the box on one line.
 * Sizes are in world units, so a label that fits keeps fitting at every zoom: zooming in only
 * makes it bigger on screen, it never truncates.
 */
export const fitLabelFontSize = ({
  lines,
  width,
  height,
  maxFontSize,
  measure = measureCanvasTextWidth,
}: {
  lines: string[];
  width: number;
  height: number;
  maxFontSize: number;
  measure?: MeasureTextWidth;
}) => {
  if (lines.length === 0 || width <= 0 || height <= 0) return 0;

  const referenceSize = 100;
  const widestLineAtReference = Math.max(...lines.map((line) => measure(line, referenceSize)));
  const byWidth =
    widestLineAtReference > 0
      ? (width * LABEL_BOX_FILL * referenceSize) / widestLineAtReference
      : maxFontSize;
  const byHeight = (height * LABEL_BOX_FILL) / (lines.length * CANVAS_LABEL_LINE_HEIGHT);

  return Math.max(0, Math.min(maxFontSize, byWidth, byHeight));
};

/**
 * Greedy word wrap at `fontSize` within `width`, as the editor's canvas label wraps. A single word
 * wider than the box is broken between characters so nothing runs past the product edge.
 */
export const wrapLabelText = (
  text: string,
  width: number,
  fontSize: number,
  measure: MeasureTextWidth = measureCanvasTextWidth,
) => {
  const usableWidth = width * LABEL_BOX_FILL;
  const fits = (candidate: string) => measure(candidate, fontSize) <= usableWidth;
  const lines: string[] = [];
  let current = "";

  const pushWord = (word: string) => {
    if (fits(word)) {
      current = word;
      return;
    }
    // Break an over-long word into the longest pieces that fit.
    let piece = "";
    for (const char of word) {
      if (piece && !fits(piece + char)) {
        lines.push(piece);
        piece = char;
      } else {
        piece += char;
      }
    }
    current = piece;
  };

  for (const word of text.split(/\s+/).filter(Boolean)) {
    const candidate = current ? `${current} ${word}` : word;
    if (fits(candidate)) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      pushWord(word);
    }
  }
  if (current) lines.push(current);

  return lines;
};

export interface FittedProductLabel {
  /** The product label, wrapped across the product; empty when there is none. */
  labelLines: string[];
  labelFontSize: number;
  value: string;
  valueFontSize: number;
}

/** Share of the product height the value gets when a product label sits above it. */
const VALUE_HEIGHT_SHARE = 0.4;

/**
 * Sizes the product label and the value separately, so a long product label cannot shrink the
 * value. The value takes one line, as large as fits its share of the box. The product label wraps
 * across the rest of the box, at the editor's size where it fits and smaller where it does not.
 * Everything is in world units, so the label keeps fitting at every zoom.
 */
export const fitProductLabel = ({
  label,
  value,
  width,
  height,
  maxFontSize,
  measure = measureCanvasTextWidth,
}: {
  label: string;
  value: string;
  width: number;
  height: number;
  maxFontSize: number;
  measure?: MeasureTextWidth;
}): FittedProductLabel => {
  const hasLabel = label.trim().length > 0;
  const hasValue = value.trim().length > 0;
  const valueHeight = hasLabel ? height * VALUE_HEIGHT_SHARE : height;
  const valueFontSize = hasValue
    ? fitLabelFontSize({ lines: [value], width, height: valueHeight, maxFontSize, measure })
    : 0;

  if (!hasLabel) {
    return { labelLines: [], labelFontSize: 0, value, valueFontSize };
  }

  const labelHeight = height - valueFontSize * CANVAS_LABEL_LINE_HEIGHT;
  const fitsAt = (fontSize: number) =>
    wrapLabelText(label, width, fontSize, measure).length * fontSize * CANVAS_LABEL_LINE_HEIGHT <=
    labelHeight * LABEL_BOX_FILL;

  // Largest wrapped size that fits the remaining height; 12 halvings is well under a pixel.
  let low = 0;
  let high = maxFontSize;
  if (fitsAt(high)) {
    low = high;
  } else {
    for (let step = 0; step < 12; step += 1) {
      const middle = (low + high) / 2;
      if (fitsAt(middle)) low = middle;
      else high = middle;
    }
  }

  return {
    labelLines: low > 0 ? wrapLabelText(label, width, low, measure) : [],
    labelFontSize: low,
    value,
    valueFontSize,
  };
};
